import React, { useEffect, useMemo, useState } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useData } from '../../context/DataContext';
import './SA_Purchases.css';

import { Search, Wallet } from 'lucide-static';

const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;

    if (size) {
      svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    }

    if (color) {
      svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    }

    if (className) {
      svgString = svgString.replace('<svg', `<svg class="${className}"`);
    }

    return svgString;
  };

  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const SA_Purchases = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const { purchases, purchasesLoaded } = useData();
  const loadingPurchases = !purchasesLoaded;
  const [error, setError] = useState('');


  const formatRelativeTime = (timeMs) => {
    if (!timeMs || Number.isNaN(timeMs)) return 'Unknown time';

    const diff = Date.now() - timeMs;
    if (diff < 60 * 1000) return 'Just now';

    const mins = Math.floor(diff / (60 * 1000));
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;

    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

    return new Date(timeMs).toLocaleString();
  };

  const filteredPurchases = useMemo(() => {
    return (purchases || []).filter((item) => {
      return [
        item.name,
        item.email,
        String(item.cost),
        item.referenceId,
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  }, [purchases, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredPurchases.length / ITEMS_PER_PAGE));

  const paginatedPurchases = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPurchases.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPurchases, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, purchases]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        <div className="logs-top-bar">
          <div className="logs-search-section">
            <div className="logs-search-wrapper">
              <Icon icon={Search} size={18} className="logs-search-icon" />
              <input
                type="text"
                placeholder="Search Purchases..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                  }
                }}
                className="logs-search-input"
              />
            </div>
          </div>
        </div>

        <div className="logs-card">
          <h1 className="card-title">Recent Purchases</h1>
          <p className="card-subtitle">Monitor product purchases and transactions across the platform</p>

          <div className="logs-list">
            {loadingPurchases ? (
              <div className="no-logs-state">
                <Icon icon={Wallet} size={48} className="no-logs-icon" />
                <span>Loading purchases...</span>
              </div>
            ) : error ? (
              <div className="no-logs">{error}</div>
            ) : filteredPurchases.length > 0 ? (
              paginatedPurchases.map((item, index) => (
                <div key={item.id || index} className="log-card">
                  <div className="log-left">
                    <div className="log-icon-wrapper" style={{ backgroundColor: 'rgba(52, 211, 153, 0.1)', color: '#10b981', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon icon={Wallet} size={24} className="log-icon" />
                    </div>
                    <div className="log-text">
                      <div className="log-title">
                        {item.name || (item.purchased ? `Avisa - ${item.purchased} Credits Bundle` : 'Credits Purchase')}
                        <span className="severity-pill severity-info" style={{ backgroundColor: 'rgba(52, 211, 153, 0.1)', color: '#10b981' }}>
                          ₱{item.cost}
                        </span>
                      </div>
                      <div className="log-details">
                        {item.anonymized || item.email === 'unknown' || item.email === 'Deleted User' || !item.email
                          ? 'Deleted User'
                          : item.email} · Ref: {item.referenceId || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="log-time">{formatRelativeTime(item.date)}</div>
                </div>
              ))
            ) : (
              <div className="no-logs">No purchases found matching your search.</div>
            )}
          </div>

          {!loadingPurchases && !error && filteredPurchases.length > 0 && (
            <div className="logs-pagination">
              <span className="logs-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="logs-pagination-actions">
                <button
                  type="button"
                  className="logs-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {(() => {
                  const maxVisiblePages = 20;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = startPage + maxVisiblePages - 1;

                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`logs-pagination-btn ${currentPage === page ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
                <button
                  type="button"
                  className="logs-pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SA_Purchases;
